import React from "react";
import { DashboardPageLayout } from "../../../components/layout/DashboardPageLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { ButtonLink } from "../../../components/ui/button";
import {
  formatAdminTableDate,
  type AdminTableDateOptions,
} from "../../../lib/formatAdminTableDate";
import type { AdminBlogPostSummary, AdminRouteMap } from "../types";

type AdminBlogIndexPageProps = {
  posts: AdminBlogPostSummary[];
  adminRoutes: AdminRouteMap;
  dateOptions: AdminTableDateOptions;
};

const STATUS_BADGE: Record<string, string> = {
  published: "bg-emerald-50 text-emerald-700 border-emerald-200",
  draft: "bg-amber-50 text-amber-700 border-amber-200",
  archived: "bg-neutral-100 text-neutral-700 border-neutral-200",
};

export const AdminBlogIndexPage: React.FC<AdminBlogIndexPageProps> = ({
  posts,
  adminRoutes,
  dateOptions,
}) => {
  const headerActions = (
    <ButtonLink to={adminRoutes.newAdminBlogPost}>New post</ButtonLink>
  );

  return (
    <DashboardPageLayout
      title="Blog"
      subtitle="Manage published and draft articles. Generate drafts with AI, then review before publishing."
      headerActions={headerActions}
    >
      <Card>
        <CardHeader>
          <CardTitle>All posts</CardTitle>
        </CardHeader>
        <CardContent>
          {posts.length === 0 ? (
            <p className="text-muted-foreground">
              No blog posts yet. Use "New post" to draft one.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 font-medium">Title</th>
                    <th className="text-left py-3 px-4 font-medium">Status</th>
                    <th className="text-left py-3 px-4 font-medium">
                      Target keyword
                    </th>
                    <th className="text-left py-3 px-4 font-medium">Slug</th>
                    <th className="text-left py-3 px-4 font-medium">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((post) => {
                    const editHref = adminRoutes.editAdminBlogPostBase.replace(
                      ":id",
                      String(post.id),
                    );
                    const badgeClass =
                      STATUS_BADGE[post.status] ?? STATUS_BADGE.draft;
                    const go = () => {
                      window.location.assign(editHref);
                    };
                    return (
                      <tr
                        key={post.id}
                        className="border-b border-border/60 last:border-0 cursor-pointer hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        tabIndex={0}
                        role="link"
                        aria-label={`Edit blog post: ${post.title}`}
                        onClick={go}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            go();
                          }
                        }}
                      >
                        <td className="py-3 px-4 font-medium text-foreground">
                          {post.title}
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold capitalize ${badgeClass}`}
                          >
                            {post.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-muted-foreground">
                          {post.targetKeyword ?? "—"}
                        </td>
                        <td className="py-3 px-4 font-mono text-xs text-muted-foreground">
                          {post.slug}
                        </td>
                        <td className="py-3 px-4 text-muted-foreground">
                          {formatAdminTableDate(post.updatedAt, dateOptions)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardPageLayout>
  );
};
